import { Box, Chip, Divider, Stack, Typography } from '@mui/material'
import SectionContainer from './SectionContainer'
import { SectionTemplate } from '../types'
import RadarChartMini from '../charts/RadarChartMini'
import { pathColor } from '../palette'

interface Path {
  title?: string
  whatItIs?: string
  whyItFits?: string
  typicalTitles?: string[]
  industries?: string[]
  positioningRequirements?: string[]
  risksAndConstraints?: string[]
  dimensions?: Record<string, number>
}

interface Props {
  template: SectionTemplate
  values: { paths?: Path[] }
  title: string
  description: string
  sectionNumber?: number
}

function ChipList({ items, label, accent }: { items?: string[]; label: string; accent: string }) {
  if (!items || items.length === 0) return null
  return (
    <Stack spacing={1}>
      <Typography variant="overline" sx={{ letterSpacing: 2, fontSize: 10, color: accent, fontWeight: 600 }}>
        {label}
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75}>
        {items.map((item, i) => (
          <Chip
            key={i}
            label={item}
            size="small"
            variant="outlined"
            sx={{ borderColor: `${accent}66`, color: '#1a1a1a' }}
          />
        ))}
      </Stack>
    </Stack>
  )
}

function Bullets({ items, label, accent }: { items?: string[]; label: string; accent: string }) {
  if (!items || items.length === 0) return null
  return (
    <Stack spacing={1}>
      <Typography variant="overline" sx={{ letterSpacing: 2, fontSize: 10, color: accent, fontWeight: 600 }}>
        {label}
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
        {items.map((item, i) => (
          <Box component="li" key={i} sx={{ mb: 0.5, '&::marker': { color: accent } }}>
            <Typography variant="body2">{item}</Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  )
}

export default function DeepDiveSection({ template, values, title, description, sectionNumber }: Props) {
  const paths = values?.paths || []
  const dimensionsField = (template.fields || [])
    .find((f) => f.type === 'repeater')
    ?.fields?.find((f) => f.type === 'dimensions')
  const axes = dimensionsField?.axes || ['Fit', 'Speed', 'Upside', 'Stability', 'Demand']

  return (
    <SectionContainer
      id={template.sectionTypeId}
      number={sectionNumber}
      title={title}
      description={description}
    >
      <Stack spacing={6}>
        {paths.map((path, i) => {
          const accent = pathColor(i)
          return (
            <Box key={i}>
              {i > 0 && <Divider sx={{ mb: 6, borderColor: 'rgba(0,0,0,0.06)' }} />}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                alignItems="stretch"
              >
                <Box sx={{ flex: 2 }}>
                  <Stack spacing={3}>
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: accent,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'var(--ns-display-font)',
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Typography
                          variant="overline"
                          sx={{
                            color: accent,
                            letterSpacing: 2,
                            fontWeight: 700,
                          }}
                        >
                          Path {i + 1}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontFamily: 'var(--ns-display-font)',
                          fontSize: { xs: 24, md: 32 },
                          fontWeight: 500,
                          lineHeight: 1.15,
                        }}
                      >
                        {path.title}
                      </Typography>
                    </Box>
                    {path.whatItIs && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ letterSpacing: 2, fontSize: 10, color: accent, fontWeight: 600 }}
                        >
                          What It Is
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                          {path.whatItIs}
                        </Typography>
                      </Box>
                    )}
                    {path.whyItFits && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ letterSpacing: 2, fontSize: 10, color: accent, fontWeight: 600 }}
                        >
                          Why It Fits
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                          {path.whyItFits}
                        </Typography>
                      </Box>
                    )}
                    <ChipList label="Typical Titles" items={path.typicalTitles} accent={accent} />
                    <ChipList label="Industries" items={path.industries} accent={accent} />
                    <Bullets label="Positioning Requirements" items={path.positioningRequirements} accent={accent} />
                    <Bullets label="Risks & Constraints" items={path.risksAndConstraints} accent={accent} />
                  </Stack>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 260,
                    background: `${accent}0d`,
                    border: `1px solid ${accent}33`,
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ letterSpacing: 2, fontSize: 10, color: accent, fontWeight: 600, mb: 1 }}
                  >
                    Dimensions
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <RadarChartMini axes={axes} values={path.dimensions || {}} color={accent} />
                  </Box>
                </Box>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </SectionContainer>
  )
}
